import React from 'react';
import { Flame, Droplet, FileSignature } from 'lucide-react';

/**
 * TrinityMacroBlocks — § II of the Home manifesto.
 *
 * THREE MASSIVE blocks on Steel Blue (#91AFC0):
 *
 *   KEWRA  · The Signal   — The Fragrance of Truth.
 *                           Real value has a scent the market cannot
 *                           ignore. Protected by thorns, guarded by
 *                           Shesha. The Architect in the marketplace.
 *
 *   KAMAL  · The State    — The Accepted Self.
 *                           The lotus accepts the mud as fuel but
 *                           rises unsullied. Noise rolls off like a
 *                           droplet. The Architect in meditation.
 *
 *   KARMA  · The Record   — The Incorruptible Record.
 *                           Action creates a trace. Every ritual is
 *                           witnessed by Sovereign IoT and inscribed
 *                           on the Timechain.
 *
 * The duality between Kewra (Intensity · Marketplace) and Kamal
 * (Peace · Meditation) is the Duality of the Sovereign Founder.
 * Each block carries a distinct visual temperature: Kewra-flame,
 * Kamal-droplet, Karma-seal.
 */

const BLOCKS = [
  {
    id: 'kewra',
    word:    'Kewra',
    role:    'Witness',
    epithet: 'The Guardian Presence',
    body: (
      <>
        Kewra stands at the intersection of the <em>scent of value</em>{' '}
        and the <em>shield of protection</em>. Rooted in the depths, it
        witnesses the foundation. Its undeniable fragrance is its own
        <span className="not-italic"> Proof of Work </span>— a Behavioral
        Oracle. The thorns are a filter, witnessing the intent of all
        who approach.
      </>
    ),
    pulse: {
      strophe: 'Thorn for defense. Steam for signal. Shesha for protection.',
      coda:    'This is the pulse of the Kewra.',
    },
    link:    'The Shield (Shesha): Guardian Infrastructure',
    motif:   'Thorn · Steam · Shesha',
    icon:    Flame,        // intensity, sharp, signaling
    accent:  '#D4AF5A',    // gold — sharp marketplace heat
    side:    'left',
  },
  {
    id: 'kamal',
    word:    'Kamal',
    role:    'State',
    epithet: 'The Accepted Self',
    body: (
      <>
        The lotus accepts the mud as <em>fuel</em>, but rises unsullied.
        Our ritual does not ask you to perform for others or alter your
        native beauty. We provide the <em>biophysical armor</em> to
        protect your original blueprint, allowing the noise of the
        industry to roll off like a droplet on a leaf.
      </>
    ),
    motif:   'Mud · Lotus · Droplet',
    icon:    Droplet,      // cool, contained, meditative
    accent:  '#F4F8F9',    // cream — quiet meditation
    side:    'centre',
  },
  {
    id: 'karma',
    word:    'Karma',
    role:    'Record',
    epithet: 'The Incorruptible Record',
    body: (
      <>
        You cannot have the <em>scent of value</em> without the{' '}
        <em>shield of protection</em>. This is why Shreesavaa uses{' '}
        <span className="not-italic font-medium">Atmosic IoT</span> and
        the <span className="not-italic font-medium">Timechain</span>.
        The Shesha (Code) protects the Fragrance (Purity).
      </>
    ),
    coupling: {
      strophe: 'Atmosic IoT · Timechain',
      coda:    'Shesha (Code) protects Fragrance (Purity).',
    },
    motif:   'IoT · Hash · Timechain',
    icon:    FileSignature,
    accent:  '#D4AF5A',
    side:    'right',
  },
];

const TrinityMacroBlocks = ({ testId = 'home-trinity' }) => (
  <section
    data-testid={testId}
    aria-label="The 3K Trinity — Kewra · Kamal · Karma"
    className="relative w-full overflow-hidden"
    style={{ backgroundColor: '#91AFC0' }}
  >
    {/* 3K Seal · large gold watermark behind the Trinity — three intersecting
        Devanagari-inspired arcs sealed by a hand-drawn circle. Quiet, never
        loud. Sits at ~0.07 opacity so the wordmarks remain primary. */}
    <svg
      aria-hidden
      viewBox="0 0 600 600"
      className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[78vw] max-w-[820px] opacity-[0.075]"
      data-testid={`${testId}-3k-seal`}
    >
      <defs>
        <radialGradient id="trinitySealHalo" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#D4AF5A" stopOpacity="0.95" />
          <stop offset="55%" stopColor="#D4AF5A" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#D4AF5A" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Soft halo */}
      <circle cx="300" cy="300" r="280" fill="url(#trinitySealHalo)" />
      {/* Outer seal ring */}
      <circle cx="300" cy="300" r="260" fill="none" stroke="#D4AF5A" strokeWidth="2.2" />
      {/* Inner ring */}
      <circle cx="300" cy="300" r="240" fill="none" stroke="#D4AF5A" strokeWidth="0.9" opacity="0.65" />
      {/* Three K arcs — one per trinity member, radially arranged at 12, 4, 8 o'clock */}
      <g fill="none" stroke="#D4AF5A" strokeWidth="3.2" strokeLinecap="round">
        {/* K-arc · Kewra (top) */}
        <g transform="rotate(-90 300 300)">
          <path d="M 220 230 L 220 370 M 220 300 L 290 230 M 220 300 L 290 370" />
        </g>
        {/* K-arc · Kamal (lower-right) */}
        <g transform="rotate(30 300 300)">
          <path d="M 220 230 L 220 370 M 220 300 L 290 230 M 220 300 L 290 370" />
        </g>
        {/* K-arc · Karma (lower-left) */}
        <g transform="rotate(150 300 300)">
          <path d="M 220 230 L 220 370 M 220 300 L 290 230 M 220 300 L 290 370" />
        </g>
      </g>
      {/* Centre dot — the meeting point */}
      <circle cx="300" cy="300" r="6" fill="#D4AF5A" />
      {/* Tri-tick marks around the seal ring */}
      <g stroke="#D4AF5A" strokeWidth="2" opacity="0.85">
        <line x1="300" y1="20"  x2="300" y2="40"  />
        <line x1="540" y1="430" x2="525" y2="421" transform="rotate(120 300 300)" />
        <line x1="540" y1="430" x2="525" y2="421" transform="rotate(240 300 300)" />
      </g>
    </svg>

    {/* Gold top hairline */}
    <span
      aria-hidden
      className="block w-full h-px relative z-10"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.6) 40%, rgba(212,175,90,0.6) 60%, transparent)',
      }}
    />

    <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
      <p
        data-testid={`${testId}-overline`}
        className="text-center font-mono text-[10px] md:text-[12px] tracking-[0.42em] uppercase font-semibold text-[#F4F8F9] mb-4 md:mb-6 flex items-center justify-center gap-4"
      >
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
        The 3K Protocol · Witness · State · Record
        <span aria-hidden className="inline-block w-10 h-px bg-[#D4AF5A]" />
      </p>
      <p
        data-testid={`${testId}-duality`}
        className="text-center font-serif italic text-[#F4F8F9]/85 mb-14 md:mb-20"
        style={{ fontSize: 'clamp(15px, 1.2vw, 20px)' }}
      >
        The duality of the Sovereign Founder —{' '}
        <span className="not-italic font-mono text-[#D4AF5A]">a witness</span>{' '}
        at the foundation,{' '}
        <span className="not-italic font-mono text-[#D4AF5A]">a state</span>{' '}
        above the mud,{' '}
        <span className="not-italic font-mono text-[#D4AF5A]">a record</span>{' '}
        on the chain.
      </p>

      <ol className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
        {BLOCKS.map((b, i) => {
          const Icon = b.icon;
          const isCentre = b.side === 'centre';
          // Subtle directional motif strip behind each macro word.
          const stripGrad = isCentre
            ? 'radial-gradient(ellipse at center, rgba(244,248,249,0.22) 0%, transparent 65%)'
            : b.side === 'left'
              ? 'linear-gradient(120deg, rgba(212,175,90,0.18), transparent 70%)'
              : 'linear-gradient(-120deg, rgba(212,175,90,0.18), transparent 70%)';
          return (
            <li
              key={b.id}
              data-testid={`${testId}-block-${b.id}`}
              className={`relative flex flex-col items-center justify-start text-center min-h-[560px] md:min-h-[640px] px-6 md:px-8 py-12 md:py-14 ${i > 0 ? 'md:border-l border-[#F4F8F9]/25' : ''} ${isCentre ? 'bg-[#7C9CB1]/35 md:scale-[1.02]' : ''}`}
            >
              {/* Background motif strip */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{ background: stripGrad }}
              />

              {/* Lotus glyph — quiet hand-drawn SVG behind the Kamal word, never on Kewra/Karma */}
              {isCentre && (
                <svg
                  aria-hidden
                  viewBox="0 0 200 200"
                  className="pointer-events-none absolute left-1/2 top-[42%] -translate-x-1/2 -translate-y-1/2 w-[78%] max-w-[420px] opacity-[0.08]"
                >
                  <g fill="none" stroke="#F4F8F9" strokeWidth="0.8" strokeLinecap="round">
                    {/* Centre petal */}
                    <path d="M100 50 C 92 80, 92 120, 100 150 C 108 120, 108 80, 100 50 Z" />
                    {/* Inner left + right petals */}
                    <path d="M100 60 C 72 85, 60 120, 70 150 C 90 130, 100 100, 100 60 Z" />
                    <path d="M100 60 C 128 85, 140 120, 130 150 C 110 130, 100 100, 100 60 Z" />
                    {/* Outer left + right petals */}
                    <path d="M100 70 C 50 95, 30 130, 45 155 C 80 140, 100 110, 100 70 Z" />
                    <path d="M100 70 C 150 95, 170 130, 155 155 C 120 140, 100 110, 100 70 Z" />
                    {/* Water-line beneath */}
                    <path d="M30 160 Q 100 175 170 160" opacity="0.6" />
                    <path d="M50 170 Q 100 180 150 170" opacity="0.35" />
                  </g>
                </svg>
              )}

              {/* Macro icon — visual temperature differs per block */}
              <Icon
                size={isCentre ? 52 : 46}
                strokeWidth={1.1}
                className="relative mb-7 md:mb-9 opacity-95"
                style={{ color: b.accent }}
                data-testid={`${testId}-block-${b.id}-icon`}
              />

              {/* Role chip — quick scannable taxonomy */}
              <p
                data-testid={`${testId}-block-${b.id}-role`}
                className="relative font-mono text-[11px] md:text-[12px] tracking-[0.46em] uppercase font-semibold mb-4 px-3 py-1.5 border bg-[#1A1A1F]/35"
                style={{ color: b.accent, borderColor: b.accent }}
              >
                The {b.role}
              </p>

              {/* MASSIVE wordmark */}
              <p
                data-testid={`${testId}-block-${b.id}-word`}
                className="relative font-serif font-light text-[#F4F8F9] leading-[0.86] tracking-[-0.02em]"
                style={{ fontSize: 'clamp(64px, 8vw, 140px)' }}
              >
                {b.word}<span style={{ color: b.accent }}>.</span>
              </p>

              {/* Italic epithet — "Architect in the marketplace / meditation" */}
              <p
                data-testid={`${testId}-block-${b.id}-epithet`}
                className="relative mt-5 md:mt-7 font-serif italic text-[#F4F8F9]/90 max-w-[20ch]"
                style={{ fontSize: 'clamp(16px, 1.3vw, 22px)' }}
              >
                {b.epithet}.
              </p>

              {/* Narrative body — the alchemical meaning */}
              <p
                data-testid={`${testId}-block-${b.id}-body`}
                className={`relative ${isCentre ? 'mt-7 md:mt-9 max-w-[40ch] text-[#F4F8F9]/90' : 'mt-6 md:mt-8 max-w-[32ch] text-[#F4F8F9]/80'} font-light leading-relaxed`}
                style={{ fontSize: isCentre ? 'clamp(15px, 1.15vw, 19px)' : 'clamp(13px, 0.95vw, 16px)' }}
              >
                {b.body}
              </p>

              {/* Core-takeaway badge — surfaces on Kamal only */}
              {isCentre && (
                <p
                  data-testid={`${testId}-block-${b.id}-takeaway`}
                  className="relative mt-7 md:mt-8 inline-flex items-center gap-3 font-mono text-[10px] md:text-[11px] tracking-[0.42em] uppercase font-semibold text-[#F4F8F9] border-y border-[#F4F8F9]/55 py-2"
                >
                  <span aria-hidden className="inline-block w-1.5 h-1.5 rotate-45 bg-[#F4F8F9]" />
                  The Accepted Self
                  <span aria-hidden className="inline-block w-1.5 h-1.5 rotate-45 bg-[#F4F8F9]" />
                </p>
              )}

              {/* Pulse strophe — the rhythmic refrain (e.g. Kewra's "Thorn · Steam · Shesha") */}
              {b.pulse && (
                <div
                  data-testid={`${testId}-block-${b.id}-pulse`}
                  className="relative mt-6 md:mt-7 px-5 py-4 border border-[#D4AF5A]/55 bg-[#1A1A1F]/30 max-w-[36ch]"
                >
                  <p
                    className="font-serif text-[#F4F8F9] leading-snug"
                    style={{ fontSize: 'clamp(14px, 1.05vw, 18px)' }}
                  >
                    {b.pulse.strophe}
                  </p>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.32em] uppercase text-[#D4AF5A]">
                    {b.pulse.coda}
                  </p>
                </div>
              )}

              {/* Coupling strophe — Karma's IoT × Timechain anchor */}
              {b.coupling && (
                <div
                  data-testid={`${testId}-block-${b.id}-coupling`}
                  className="relative mt-6 md:mt-7 max-w-[36ch] w-full"
                >
                  {/* IoT/hash data-ribbon — mock signal trace + block tick */}
                  <svg
                    aria-hidden
                    viewBox="0 0 280 40"
                    className="w-full h-9 mb-3"
                  >
                    <g stroke="#D4AF5A" strokeWidth="1.2" fill="none" strokeLinecap="round">
                      {/* Sensor pulse trace */}
                      <path
                        d="M0 22 L 40 22 L 48 12 L 56 28 L 64 22 L 110 22 L 118 8 L 126 32 L 134 22 L 200 22 L 208 14 L 216 26 L 224 22 L 280 22"
                        opacity="0.85"
                      />
                      {/* Block tick marks */}
                      <line x1="40"  y1="36" x2="40"  y2="32" opacity="0.7" />
                      <line x1="110" y1="36" x2="110" y2="32" opacity="0.7" />
                      <line x1="200" y1="36" x2="200" y2="32" opacity="0.7" />
                      <line x1="270" y1="36" x2="270" y2="32" opacity="0.7" />
                    </g>
                    {/* Block hash glyph */}
                    <g fill="#D4AF5A">
                      <rect x="38"  y="36" width="4" height="3" opacity="0.7" />
                      <rect x="108" y="36" width="4" height="3" opacity="0.7" />
                      <rect x="198" y="36" width="4" height="3" opacity="0.7" />
                      <rect x="268" y="36" width="4" height="3" opacity="0.7" />
                    </g>
                  </svg>

                  {/* Tickers ─ IoT signal · ledger anchor */}
                  <div className="flex items-center justify-between px-1 mb-3 font-mono text-[9px] tracking-[0.3em] uppercase text-[#F4F8F9]/65">
                    <span className="flex items-center gap-1.5">
                      <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF5A] animate-pulse" />
                      Atmosic IoT
                    </span>
                    <span className="flex items-center gap-1.5">
                      Timechain
                      <span aria-hidden className="inline-block w-1.5 h-1.5 bg-[#D4AF5A]" />
                    </span>
                  </div>

                  <div className="px-5 py-4 border border-[#D4AF5A]/55 bg-[#1A1A1F]/30">
                    <p
                      className="font-serif text-[#F4F8F9] leading-snug"
                      style={{ fontSize: 'clamp(14px, 1.05vw, 18px)' }}
                    >
                      <span className="font-mono not-italic text-[10px] tracking-[0.3em] uppercase text-[#D4AF5A] block mb-1">
                        {b.coupling.strophe}
                      </span>
                      {b.coupling.coda}
                    </p>
                  </div>
                </div>
              )}

              {/* Optional Protocol link — surfaces e.g. "The Shield (Shesha): Guardian Infrastructure" */}
              {b.link && (
                <p
                  data-testid={`${testId}-block-${b.id}-link`}
                  className="relative mt-5 inline-flex items-center gap-2 font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#D4AF5A] border-t border-[#D4AF5A]/40 pt-3"
                >
                  <span aria-hidden className="inline-block w-1 h-1 rotate-45 bg-[#D4AF5A]" />
                  {b.link}
                </p>
              )}

              {/* Visual motif chip — Kewra/Kamal/Karma signatures */}
              <p
                data-testid={`${testId}-block-${b.id}-motif`}
                className="relative mt-7 md:mt-9 inline-flex items-center gap-2 font-mono text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/65 border-t border-[#F4F8F9]/25 pt-3"
              >
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: b.accent }}
                />
                {b.motif}
              </p>
            </li>
          );
        })}
      </ol>
    </div>

    {/* Gold bottom hairline */}
    <span
      aria-hidden
      className="block w-full h-px"
      style={{
        background:
          'linear-gradient(90deg, transparent, rgba(212,175,90,0.6) 40%, rgba(212,175,90,0.6) 60%, transparent)',
      }}
    />
  </section>
);

export default TrinityMacroBlocks;
